export type ThemePreference = 'auto' | 'light' | 'dark'
export type UiDensityPreference = 'compact' | 'normal' | 'comfortable'

const STORAGE_KEY = 'nr01.theme'
const THEME_EXPLICIT_KEY = 'nr01.theme.explicit'
const UI_DENSITY_STORAGE_KEY = 'nr01.ui-density'

const UI_DENSITY_VALUES = new Set<UiDensityPreference>(['compact', 'normal', 'comfortable'])

function safe_local_storage(): Storage | null {
  try {
    if (typeof localStorage === 'undefined') return null
    return localStorage
  } catch {
    return null
  }
}

function theme_explicitly_chosen(storage: Storage | null): boolean {
  if (!storage) return false
  try {
    return String(storage.getItem(THEME_EXPLICIT_KEY) || '').trim() === '1'
  } catch {
    return false
  }
}

function normalize_theme_preference(raw: string, explicit: boolean): ThemePreference {
  if (raw === 'dark' || raw === 'auto') return raw
  if (raw === 'light') {
    // Versões antigas gravavam "light" como padrão implícito — respeitar o SO até escolha explícita.
    return explicit ? 'light' : 'auto'
  }
  return 'auto'
}

export function get_theme_preference(): ThemePreference {
  const storage = safe_local_storage()
  const raw = storage ? String(storage.getItem(STORAGE_KEY) || '').trim().toLowerCase() : ''
  return normalize_theme_preference(raw, theme_explicitly_chosen(storage))
}

export function get_ui_density_preference(): UiDensityPreference {
  const storage = safe_local_storage()
  const raw = storage ? String(storage.getItem(UI_DENSITY_STORAGE_KEY) || '').trim().toLowerCase() : ''
  if (UI_DENSITY_VALUES.has(raw as UiDensityPreference)) return raw as UiDensityPreference
  return 'normal'
}

function prefers_dark(): boolean {
  try {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  } catch {
    return false
  }
}

export function resolve_theme(preference: ThemePreference): 'light' | 'dark' {
  if (preference === 'light' || preference === 'dark') return preference
  return prefers_dark() ? 'dark' : 'light'
}

function set_meta_theme_color(resolved: 'light' | 'dark'): void {
  const meta = document.getElementById('nr01-theme-color')
  if (!meta) return
  meta.setAttribute('content', resolved === 'dark' ? '#061a32' : '#0b2542')
}

export function apply_ui_density(preference: UiDensityPreference): void {
  const normalized = UI_DENSITY_VALUES.has(preference) ? preference : 'normal'
  document.documentElement.dataset.uiDensity = normalized
}

export function apply_theme(preference: ThemePreference): void {
  const resolved = resolve_theme(preference)
  document.documentElement.dataset.theme = resolved
  document.documentElement.dataset.themePref = preference
  document.documentElement.style.colorScheme = resolved
  document.documentElement.classList.toggle('dark', resolved === 'dark')
  set_meta_theme_color(resolved)
}

export function set_theme_preference(preference: ThemePreference): void {
  const storage = safe_local_storage()
  if (storage) {
    try {
      storage.setItem(STORAGE_KEY, preference)
      storage.setItem(THEME_EXPLICIT_KEY, '1')
    } catch {
      // ignore
    }
  }
  apply_theme(preference)
  window.dispatchEvent(new CustomEvent('nr01:theme', { detail: { preference } }))
}

export function set_ui_density_preference(preference: UiDensityPreference): void {
  const storage = safe_local_storage()
  if (storage) {
    try {
      storage.setItem(UI_DENSITY_STORAGE_KEY, preference)
    } catch {
      // ignore
    }
  }
  apply_ui_density(preference)
  window.dispatchEvent(new CustomEvent('nr01:ui-density', { detail: { preference } }))
}

export function next_theme_preference(preference: ThemePreference): ThemePreference {
  if (preference === 'auto') return 'light'
  if (preference === 'light') return 'dark'
  return 'auto'
}

export function next_ui_density_preference(preference: UiDensityPreference): UiDensityPreference {
  if (preference === 'compact') return 'normal'
  if (preference === 'normal') return 'comfortable'
  return 'compact'
}

export function theme_preference_label(preference: ThemePreference): string {
  if (preference === 'light') return 'Claro'
  if (preference === 'dark') return 'Escuro'
  return 'Automático'
}

export function theme_preference_icon(preference: ThemePreference): 'auto' | 'sun' | 'moon' {
  if (preference === 'light') return 'sun'
  if (preference === 'dark') return 'moon'
  return 'auto'
}

export function ui_density_preference_label(preference: UiDensityPreference): string {
  if (preference === 'compact') return 'Compacto'
  if (preference === 'comfortable') return 'Confortável'
  return 'Normal'
}

export function ui_density_preference_icon(
  preference: UiDensityPreference
): 'density-compact' | 'density-normal' | 'density-comfortable' {
  if (preference === 'compact') return 'density-compact'
  if (preference === 'comfortable') return 'density-comfortable'
  return 'density-normal'
}

export function init_theme(): void {
  const pref = get_theme_preference()
  apply_theme(pref)
  apply_ui_density(get_ui_density_preference())

  try {
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => {
      const current = get_theme_preference()
      if (current === 'auto') apply_theme('auto')
    }
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', handler)
    } else if (typeof (media as MediaQueryList & { addListener?: (fn: () => void) => void }).addListener === 'function') {
      ;(media as MediaQueryList & { addListener: (fn: () => void) => void }).addListener(handler)
    }
  } catch {
    // ignore
  }
}